import React from 'react';
import { motion } from 'framer-motion';
import { FaHeart, FaPaw, FaAward, FaHandHoldingMedical, FaLinkedin, FaInstagram } from 'react-icons/fa';
import IconWrapper from '../components/IconWrapper';

// Animasyon için varyantlar
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: "easeOut"
    }
  })
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const Hakkimizda: React.FC = () => {
  // Değerlerimiz
  const values = [
    {
      icon: FaHeart,
      title: "Sevgi & Şefkat",
      description: "Her hayvanı kendi evcil dostumuz gibi sevgi ve şefkatle tedavi ediyoruz."
    },
    {
      icon: FaPaw,
      title: "Profesyonellik",
      description: "En son tıbbi bilgiler ve ekipmanlarla profesyonel hizmet sunuyoruz."
    },
    {
      icon: FaAward,
      title: "Kalite",
      description: "Her zaman en kaliteli bakım ve tedaviyi sağlamak için çabalıyoruz."
    },
    {
      icon: FaHandHoldingMedical,
      title: "Sorumluluk",
      description: "Hayvan sağlığı ve refahı konusunda büyük sorumluluk taşıyoruz."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-dark text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">
              Hakkımızda
            </h1>
            <p className="text-xl text-blue-100">
              2023'den beri evcil dostlarınızın sağlığı için çalışıyoruz
            </p>
          </div>
        </div>
      </section>

      {/* Misyon & Vizyon Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div 
              className="bg-white p-8 rounded-2xl shadow-md"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-display font-bold text-gray-800 mb-4">Misyonumuz</h2>
              <p className="text-gray-600 leading-relaxed">
                Evcil hayvanların sağlığı ve mutluluğu için en yüksek kalitede veterinerlik hizmetleri sunmak, 
                modern tıbbi teknolojiler ve uzman kadromuzla her hastamıza özel çözümler üretmek ve 
                hayvan sevgisini toplumda yaygınlaştırmak için çalışmak.
              </p>
            </motion.div>
            
            <motion.div 
              className="bg-white p-8 rounded-2xl shadow-md"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-display font-bold text-gray-800 mb-4">Vizyonumuz</h2>
              <p className="text-gray-600 leading-relaxed">
                Bursa'nın en güvenilir ve tercih edilen veteriner kliniği olmak, 
                sürekli gelişen teknoloji ve bilimsel yaklaşımlarla hizmet kalitemizi artırmak ve 
                evcil hayvan sağlığı konusunda bölgemizde öncü olmak.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Değerlerimiz Section */}
      <section className="py-16 bg-gray-100">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-display font-bold text-center text-gray-800 mb-12">
            Değerlerimiz
          </h2>
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {values.map((value, index) => (
              <motion.div 
                key={index}
                className="bg-white p-6 rounded-xl shadow-md text-center"
                variants={fadeIn}
                custom={index}
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-light/20 flex items-center justify-center">
                  <IconWrapper icon={value.icon} className="text-2xl text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Kliniğimiz Section */}
      <section className="py-16 bg-gray-100">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-display font-bold text-center text-gray-800 mb-12">
            Modern Kliniğimiz
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div 
              className="rounded-xl overflow-hidden shadow-md"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.unsplash.com/photo-1584555613497-9ecf9dd06f68?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80&fm=webp" 
                alt="Dijital Röntgen" 
                className="w-full h-64 object-cover"
                loading="eager"
              />
              <div className="p-4 bg-white">
                <h3 className="font-semibold text-gray-800">Dijital Röntgen & Görüntüleme</h3>
                <p className="text-gray-600">Modern görüntüleme sistemleri ile hassas teşhis</p>
              </div>
            </motion.div>
            
            <motion.div 
              className="rounded-xl overflow-hidden shadow-md"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80&fm=webp" 
                alt="Muayene Odası" 
                className="w-full h-64 object-cover"
                loading="eager"
              />
              <div className="p-4 bg-white">
                <h3 className="font-semibold text-gray-800">Muayene Odaları</h3>
                <p className="text-gray-600">Tam donanımlı muayene üniteleri</p>
              </div>
            </motion.div>
            
            <motion.div 
              className="rounded-xl overflow-hidden shadow-md"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80&fm=webp" 
                alt="Ameliyathane" 
                className="w-full h-64 object-cover"
                loading="eager"
              />
              <div className="p-4 bg-white">
                <h3 className="font-semibold text-gray-800">Modern Ameliyathane</h3>
                <p className="text-gray-600">Steril ve teknolojik cerrahi ünitesi</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Hakkimizda; 